import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { FiPlus, FiEdit, FiTrash2, FiUsers, FiFileText, FiClock, FiCheckCircle, FiAward, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { adminAPI } from '../../../services/api';
import './TestsPage.css';

const TestsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canEdit = user?.role === 'admin';
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [filter, setFilter] = useState({ difficulty: '' });

    useEffect(() => {
        loadTests();
    }, [filter]);

    const loadTests = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getTests(filter);
            setTests(response.data.tests || []);
        } catch (error) {
            console.error('Ошибка загрузки тестов:', error);
        } finally {
            setLoading(false);
        }
    };

    const persistTestsOrder = async (orderedTests) => {
        await Promise.all(
            orderedTests.map((test, index) => adminAPI.updateTestSortOrder(test.id, index))
        );
    };

    const reorderByIndexes = async (fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
        try {
            setReordering(true);
            const reordered = [...tests];
            const [movedTest] = reordered.splice(fromIndex, 1);
            reordered.splice(toIndex, 0, movedTest);
            setTests(reordered);
            await persistTestsOrder(reordered);
            await loadTests();
        } catch (error) {
            console.error('Ошибка перемещения теста:', error);
            alert('Ошибка при изменении порядка тестов');
        } finally {
            setReordering(false);
        }
    };

    const handleMoveTestUp = (index) => {
        if (index === 0) return;
        reorderByIndexes(index, index - 1);
    };

    const handleMoveTestDown = (index) => {
        if (index === tests.length - 1) return;
        reorderByIndexes(index, index + 1);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот тест?')) return;

        try {
            await adminAPI.deleteTestBlocks(id);
            alert('Тест удален');
            loadTests();
        } catch (error) {
            console.error('Ошибка удаления теста:', error);
            alert('Ошибка при удалении теста');
        }
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            beginner: '#52c41a',
            elementary: '#73d13d',
            'pre-intermediate': '#faad14',
            intermediate: '#fa8c16',
            'upper-intermediate': '#f5222d',
            advanced: '#cf1322'
        };
        return colors[difficulty] || '#999';
    };

    if (loading) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>;
    }

    return (
        <div className="tests-page">
            <div className="tests-page-header">
                <h1>Независимые тесты</h1>
                {canEdit && (
                    <button type="button" onClick={() => navigate('/admin/tests/create')} className="btn-create-test">
                        <FiPlus /> Создать тест
                    </button>
                )}
            </div>

            <div className="card tests-filter-card">
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Сложность</label>
                    <select
                        value={filter.difficulty}
                        onChange={(e) => setFilter({ ...filter, difficulty: e.target.value })}
                    >
                        <option value="">Все уровни</option>
                        <option value="beginner">Beginner</option>
                        <option value="elementary">Elementary</option>
                        <option value="pre-intermediate">Pre-Intermediate</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="upper-intermediate">Upper-Intermediate</option>
                        <option value="advanced">Advanced</option>
                    </select>
                </div>
            </div>

            {tests.length === 0 ? (
                <div className="card tests-empty">
                    <p>
                        {filter.difficulty
                            ? 'Нет тестов с выбранными фильтрами'
                            : 'Нет созданных тестов. Создайте первый тест!'}
                    </p>
                </div>
            ) : (
                <div className="tests-list">
                    {tests.map((test, index) => (
                        <div key={test.id} className="card test-card">
                            <div className="test-card-header">
                                {canEdit && (
                                    <div className="test-card-order">
                                        <span className="test-card-order-num">{index + 1}</span>
                                        <div className="test-card-order-buttons">
                                            <button
                                                type="button"
                                                className="btn-test-move"
                                                onClick={() => handleMoveTestUp(index)}
                                                disabled={index === 0 || reordering}
                                                title="Переместить выше"
                                            >
                                                <FiArrowUp />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-test-move"
                                                onClick={() => handleMoveTestDown(index)}
                                                disabled={index === tests.length - 1 || reordering}
                                                title="Переместить ниже"
                                            >
                                                <FiArrowDown />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <h3 className="test-card-title">{test.title}</h3>
                                <span
                                    className="test-card-difficulty"
                                    style={{ background: getDifficultyColor(test.difficulty) }}
                                >
                                    {test.difficulty}
                                </span>
                                <span className="test-card-blocks-badge">
                                    {test.blocks_count || 0} {test.blocks_count === 1 ? 'блок' : 'блоков'}
                                </span>
                            </div>

                            {test.description && (
                                <p className="test-card-description">{test.description}</p>
                            )}

                            <div className="test-card-meta">
                                <span>
                                    <FiFileText size={14} /> Блоков: {test.blocks_count || 0}
                                </span>
                                {test.time_limit > 0 && (
                                    <span>
                                        <FiClock size={14} /> Время: {test.time_limit} мин
                                    </span>
                                )}
                                <span>
                                    <FiCheckCircle size={14} /> Проходной балл: {test.passing_score}%
                                </span>
                                {test.level_name && (
                                    <span>
                                        <FiAward size={14} /> {test.level_name}
                                    </span>
                                )}
                            </div>

                            <div className="test-card-actions">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/admin/tests/${test.id}/results`)}
                                    className="btn-test-results"
                                    title="Результаты"
                                >
                                    <FiUsers /> Результаты
                                </button>
                                {canEdit && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/admin/tests/${test.id}/edit`)}
                                            className="btn-test-edit"
                                            title="Редактировать"
                                        >
                                            <FiEdit /> Изменить
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(test.id)}
                                            className="btn-test-delete"
                                            title="Удалить"
                                        >
                                            <FiTrash2 /> Удалить
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TestsPage;
